import { dealsApi } from './dealsApi';
import { triggerRefresh } from '@/features/table/dealsTableSlice';

/**
 * Debtor Automation API
 * Handles enabling/disabling debtor automation, resyncing automation state, and running debtor management syncs for a given deal.
 */
export const debtorAutomationApi = dealsApi.injectEndpoints({
	endpoints: (builder) => ({
		/**
		 * Fetches the current debtor automation settings and sync state for a specific deal.
		 * Also includes upcomingReminders and upcomingRemindersUnavailable from the Functions read model.
		 */
		getDebtorAutomation: builder.query({
			providesTags: (result, error, { dealId }) => [
				{ type: 'DEBTOR_AUTOMATION', id: dealId },
			],
			query: ({ dealId }) => ({
				url: `${process.env.REACT_APP_DEBTOR_API}/${dealId}/automation`,
				method: 'GET',
				params: { dealId },
			}),
			transformErrorResponse: (response) => {
				console.log('👾 ~ transform getDebtorAutomation error response', response);
				return response;
			},
		}),

		/**
		 * Enables debtor automation for a specific deal.
		 * Optimistically updates the cache to reflect the change immediately, and rolls back if the API call fails.
		 */
		enableDebtorAutomation: builder.mutation({
			invalidatesTags: (result, error, { dealId }) => [
				{ type: 'DEBTOR_AUTOMATION', id: dealId },
			],
			query: ({ dealId }) => ({
				url: `${process.env.REACT_APP_DEBTOR_API}/${dealId}/automation/enable`,
				method: 'POST',
				body: { dealId },
			}),
			onQueryStarted: async ({ dealId }, { dispatch, queryFulfilled }) => {
				await optimisicallyToggleAutomation(dispatch, queryFulfilled, dealId, true);
			},
		}),

		/**
		 * Disables debtor automation for a specific deal.
		 * Optimistically updates the cache to reflect the change immediately, and rolls back if the API call fails.
		 */
		disableDebtorAutomation: builder.mutation({
			invalidatesTags: (result, error, { dealId }) => [
				{ type: 'DEBTOR_AUTOMATION', id: dealId },
			],
			query: ({ dealId }) => ({
				url: `${process.env.REACT_APP_DEBTOR_API}/${dealId}/automation/disable`,
				method: 'POST',
				body: { dealId },
			}),
			onQueryStarted: async ({ dealId }, { dispatch, queryFulfilled }) => {
				await optimisicallyToggleAutomation(dispatch, queryFulfilled, dealId, false);
			},
		}),

		/**
		 * Runs a debtor management sync for debtor automation on a specific deal.
		 * Invalidates the cache to ensure the latest automation state is fetched.
		 */
		runDebtorAutomationSync: builder.mutation({
			invalidatesTags: (result, error, { dealId }) => [
				{ type: 'DEBTOR_AUTOMATION', id: dealId },
			],
			query: ({ dealId }) => ({
				url: `${process.env.REACT_APP_DEBTOR_API}/${dealId}/automation/sync`,
				method: 'POST',
				body: { dealId },
			}),
		}),

		/**
		 * Lists debtor reminder file upload records for a deal, optionally filtered by status.
		 */
		listDebtorReminders: builder.query({
			providesTags: (result, error, { dealId }) => [
				{ type: 'DEBTOR_REMINDERS', id: dealId },
			],
			query: ({ dealId, status, latest }) => ({
				url: `${process.env.REACT_APP_DEBTOR_API}/${dealId}/reminders/list`,
				method: 'POST',
				body: { dealId, status, latest },
			}),
			transformErrorResponse: (response) => {
				console.log('👾 ~ transform listDebtorReminders error response', response);
				return response;
			},
			transformResponse: (response) => {
				console.log('👾 ~ transform listDebtorReminders response', response);
				return response?.debtorReminders ?? [];
			},
		}),

		/**
		 * Updates the status of a debtor reminder file upload record, e.g. after a manual SharePoint save.
		 */
		updateDebtorReminder: builder.mutation({
			invalidatesTags: (result, error, { dealId }) => [
				{ type: 'DEBTOR_REMINDERS', id: dealId },
			],
			query: ({ dealId, docId, status }) => ({
				url: `${process.env.REACT_APP_DEBTOR_API}/${dealId}/reminders/update`,
				method: 'POST',
				body: { dealId, docId, status },
			}),
			transformErrorResponse: (response) => {
				console.log('👾 ~ transform updateDebtorReminder error response', response);
				return response;
			},
			transformResponse: (response) => {
				console.log('👾 ~ transform updateDebtorReminder response', response);
				return response;
			},
		}),
	}),
	overrideExisting: false,
});

/**
 * Optimistically toggle automation in cache; refresh InstantSearch after success so the list catches Typesense.
 */
async function optimisicallyToggleAutomation(dispatch, queryFulfilled, dealId, isEnabling) {
	const automationPatch = dispatch(
		debtorAutomationApi.util.updateQueryData('getDebtorAutomation', { dealId }, (draft) => {
			if (draft) {
				draft.automationEnabled = isEnabling;
			}
		}),
	);

	const dealRowPatch = dispatch(
		dealsApi.util.updateQueryData('getDealRow', dealId, (draft) => {
			if (draft) {
				draft.automationEnabled = isEnabling;
			}
		}),
	);

	try {
		await queryFulfilled;
		setTimeout(() => {
			dispatch(triggerRefresh());
		}, 1000);
	} catch (error) {
		automationPatch.undo();
		dealRowPatch.undo();
	}
}

export const {
	useGetDebtorAutomationQuery,
	useEnableDebtorAutomationMutation,
	useDisableDebtorAutomationMutation,
	useRunDebtorAutomationSyncMutation,
	useListDebtorRemindersQuery,
	useUpdateDebtorReminderMutation,
} = debtorAutomationApi;
